import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "@/utils/axiosInstance";
import { handleThunkError } from "@/utils/thunkErrorHandler";
import type {
  EstablishmentBillingConfig,
  BillingPaymentsResponse,
  BillingPaymentFilters,
  ProcessPaymentRequest,
  ProcessPaymentResponse,
  BillingStatus,
  UpdateBillingConfigRequest,
  BillingCard,
  CreateBillingCardRequest,
  BillingTokenStatusResponse,
} from "@/types/billing";
import type {
  AsaasSubaccountCreatePayload,
  AsaasDocumentsResponse,
  UploadBillingDocumentPayload,
  AsaasKycStatus,
  AsaasDocumentRequirement,
  AsaasDocumentFileInfo,
} from "@/types";

// Note: axiosInstance baseURL is set to `${API_URL}/establishment/{slug}` via AuthContext.
// Therefore, billing endpoints are relative: `/billing/...` (do NOT prefix with slug or /1).

const normalizeDocumentFiles = (
  docId: string | number,
  files: unknown,
): AsaasDocumentFileInfo[] => {
  if (!Array.isArray(files)) {
    return [];
  }

  return (files as any[]).map((file, index) => {
    const fileId =
      file?.id ??
      file?.fileId ??
      file?.file_id ??
      `${String(docId)}-file-${index}`;

    return {
      ...file,
      id: fileId,
      name:
        file?.name ??
        file?.filename ??
        file?.originalName ??
        file?.original_name ??
        `Arquivo ${index + 1}`,
      status:
        file?.status ?? file?.state ?? file?.situation ?? file?.documentStatus,
    } as AsaasDocumentFileInfo;
  });
};

const normalizeDocumentsPayload = (payload: any): AsaasDocumentsResponse => {
  let documentsSource: any[] = [];
  let rejectReasons: Record<string, unknown> | null = null;

  const unwrap = payload?.data ?? payload;

  if (Array.isArray(unwrap)) {
    documentsSource = unwrap;
  } else if (Array.isArray(unwrap?.documents)) {
    documentsSource = unwrap.documents;
    rejectReasons = (unwrap.rejectReasons ??
      unwrap.reject_reasons ??
      null) as Record<string, unknown> | null;
  } else if (Array.isArray(unwrap?.data)) {
    documentsSource = unwrap.data;
    rejectReasons = (unwrap.rejectReasons ??
      unwrap.reject_reasons ??
      unwrap.data?.rejectReasons ??
      null) as Record<string, unknown> | null;
  } else {
    rejectReasons = (unwrap?.rejectReasons ??
      unwrap?.reject_reasons ??
      null) as Record<string, unknown> | null;
  }

  const normalized: AsaasDocumentRequirement[] = documentsSource.map(
    (doc: any, index: number) => {
      const docId =
        doc?.id ??
        doc?.documentId ??
        doc?.document_id ??
        doc?.type ??
        `doc-${index}`;

      const files = normalizeDocumentFiles(docId, doc?.files ?? doc?.fileList);

      return {
        ...doc,
        id: docId,
        documentId: doc?.documentId ?? doc?.document_id ?? docId,
        name:
          doc?.name ??
          doc?.title ??
          doc?.label ??
          doc?.type ??
          `Documento ${index + 1}`,
        status:
          doc?.status ??
          doc?.documentStatus ??
          doc?.situation ??
          doc?.state ??
          doc?.current_status ??
          "",
        description: doc?.description ?? doc?.obs ?? doc?.note ?? doc?.message,
        rejectionReason:
          doc?.rejectionReason ?? doc?.rejection_reason ?? doc?.reason,
        files,
      } as AsaasDocumentRequirement;
    },
  );

  return {
    documents: normalized,
    rejectReasons,
  };
};

export const fetchBillingConfig = createAsyncThunk<
  { data: EstablishmentBillingConfig },
  void
>("establishmentBilling/fetchConfig", async (_: void, thunkAPI) => {
  try {
    // Fetch both endpoints and merge results for completeness
    const [billingResult, subsResult] = await Promise.allSettled([
      axiosInstance.get("/billing/config"),
      axiosInstance.get("/subscriptions/config"),
    ]);

    const billingRaw =
      billingResult.status === "fulfilled"
        ? (billingResult.value?.data?.data ?? billingResult.value?.data)
        : undefined;
    const subsRaw =
      subsResult.status === "fulfilled"
        ? (subsResult.value?.data?.data ?? subsResult.value?.data)
        : undefined;

    if (!billingRaw && !subsRaw) {
      throw new Error("Config endpoints failed");
    }

    const normalizeAny = (raw: any): EstablishmentBillingConfig => {
      if (
        raw &&
        typeof raw === "object" &&
        "establishment" in raw &&
        "plan" in raw &&
        "billing_config" in raw &&
        "next_payment" in raw &&
        "payment_sources" in raw
      ) {
        return raw as EstablishmentBillingConfig;
      }

      const est = raw?.establishment ?? {};
      const plan = raw?.plan ?? {};
      const billingCfg = raw?.billing_config ?? {};
      const paymentSources = raw?.payment_sources ?? {};

      const availableBalance = Number(
        billingCfg?.available_balance ??
          raw?.available_balance ??
          raw?.balance ??
          0,
      );

      const nextPaymentRaw = raw?.next_payment ?? {};
      const nextDueDate =
        nextPaymentRaw?.due_date ??
        billingCfg?.next_due_date ??
        raw?.next_due_date ??
        "";
      const nextAmount = Number(
        nextPaymentRaw?.amount ?? raw?.next_payment_amount ?? plan?.price ?? 0,
      );
      const nextIsOverdue = Boolean(
        nextPaymentRaw?.is_overdue ?? raw?.is_overdue ?? false,
      );
      const nextDaysUntil = Number(
        nextPaymentRaw?.days_until_due ?? raw?.days_until_due ?? 0,
      );

      return {
        establishment: {
          id: est?.id ?? raw?.establishment_id ?? 0,
          name: est?.name ?? "",
          status: est?.status ?? raw?.status ?? raw?.approval_status ?? "",
        },
        plan: {
          name: plan?.name ?? "",
          price: Number(plan?.price ?? raw?.plan_price ?? 0),
          period: plan?.period ?? raw?.period ?? "",
        },
        billing_config: {
          preferred_charge_source:
            billingCfg?.preferred_charge_source ??
            raw?.preferred_charge_source ??
            "system",
          billing_status:
            billingCfg?.billing_status ?? raw?.billing_status ?? "active",
          trial_ends_at:
            billingCfg?.trial_ends_at ?? raw?.trial_ends_at ?? null,
          trial_started_at:
            billingCfg?.trial_started_at ?? raw?.trial_started_at ?? null,
          free_trial_months:
            billingCfg?.free_trial_months ?? raw?.free_trial_months,
          last_paid_at: billingCfg?.last_paid_at ?? raw?.last_paid_at ?? null,
          next_due_date: nextDueDate ?? "",
          available_balance: availableBalance,
          percentage_to_pay:
            billingCfg?.percentage_to_pay ?? raw?.percentage_to_pay,
          simultaneous_schedule_limit:
            billingCfg?.simultaneous_schedule_limit ??
            raw?.simultaneous_schedule_limit,
          sale_commission_percentage:
            billingCfg?.sale_commission_percentage ??
            raw?.sale_commission_percentage,
          company_type: billingCfg?.company_type ?? raw?.company_type,
          monthly_revenue: billingCfg?.monthly_revenue ?? raw?.monthly_revenue,
          birth_date: billingCfg?.birth_date ?? raw?.birth_date ?? null,
          pix_key_type: billingCfg?.pix_key_type ?? raw?.pix_key_type ?? null,
          pix_key: billingCfg?.pix_key ?? raw?.pix_key ?? null,
          approval_status: billingCfg?.approval_status ?? raw?.approval_status,
          created_at: billingCfg?.created_at ?? raw?.created_at,
          updated_at: billingCfg?.updated_at ?? raw?.updated_at,
        },
        next_payment: {
          due_date: nextDueDate ?? "",
          amount: isFinite(nextAmount) ? nextAmount : 0,
          is_overdue: nextIsOverdue,
          days_until_due: isFinite(nextDaysUntil) ? nextDaysUntil : 0,
        },
        payment_sources: {
          system: {
            label: "Cartão de Crédito (Sistema)",
            description: "Cobrança automática via cartão de crédito do sistema",
            enabled: true,
            default_card:
              paymentSources?.system?.default_card ?? raw?.default_card ?? null,
          },
          establishment_balance: {
            label: "Saldo da Plataforma",
            description:
              "Desconto automático do saldo disponível na plataforma",
            enabled: paymentSources?.establishment_balance?.enabled ?? true,
            current_balance: Number(
              paymentSources?.establishment_balance?.current_balance ??
                availableBalance ??
                0,
            ),
          },
        },
      } as EstablishmentBillingConfig;
    };

    const cfgBilling = billingRaw ? normalizeAny(billingRaw) : undefined;
    const cfgSubs = subsRaw ? normalizeAny(subsRaw) : undefined;

    if (cfgBilling && !cfgSubs) return { data: cfgBilling } as any;
    if (!cfgBilling && cfgSubs) return { data: cfgSubs } as any;

    // Merge both configs: prefer subscriptions for plan/next_due; prefer billing for balances
    const a = cfgBilling as EstablishmentBillingConfig;
    const b = cfgSubs as EstablishmentBillingConfig;

    const merged: EstablishmentBillingConfig = {
      establishment: {
        id: a.establishment.id || b.establishment.id,
        name: a.establishment.name || b.establishment.name,
        status: a.establishment.status || b.establishment.status,
      },
      plan: {
        name: b.plan.name || a.plan.name,
        price: b.plan.price || a.plan.price,
        period: b.plan.period || a.plan.period,
      },
      billing_config: {
        preferred_charge_source:
          b.billing_config.preferred_charge_source ||
          a.billing_config.preferred_charge_source,
        billing_status:
          b.billing_config.billing_status || a.billing_config.billing_status,
        trial_ends_at:
          b.billing_config.trial_ends_at ??
          a.billing_config.trial_ends_at ??
          null,
        trial_started_at:
          b.billing_config.trial_started_at ??
          a.billing_config.trial_started_at,
        free_trial_months:
          b.billing_config.free_trial_months ??
          a.billing_config.free_trial_months,
        last_paid_at:
          b.billing_config.last_paid_at ??
          a.billing_config.last_paid_at ??
          null,
        next_due_date:
          b.billing_config.next_due_date || a.billing_config.next_due_date,
        available_balance:
          a.billing_config.available_balance ||
          b.billing_config.available_balance ||
          0,
        percentage_to_pay:
          b.billing_config.percentage_to_pay ??
          a.billing_config.percentage_to_pay,
        simultaneous_schedule_limit:
          b.billing_config.simultaneous_schedule_limit ??
          a.billing_config.simultaneous_schedule_limit,
        sale_commission_percentage:
          b.billing_config.sale_commission_percentage ??
          a.billing_config.sale_commission_percentage,
        company_type:
          b.billing_config.company_type ?? a.billing_config.company_type,
        monthly_revenue:
          b.billing_config.monthly_revenue ?? a.billing_config.monthly_revenue,
        birth_date:
          b.billing_config.birth_date ?? a.billing_config.birth_date ?? null,
        pix_key_type:
          b.billing_config.pix_key_type ??
          a.billing_config.pix_key_type ??
          null,
        pix_key: b.billing_config.pix_key ?? a.billing_config.pix_key ?? null,
        approval_status:
          b.billing_config.approval_status ?? a.billing_config.approval_status,
        created_at: b.billing_config.created_at ?? a.billing_config.created_at,
        updated_at: b.billing_config.updated_at ?? a.billing_config.updated_at,
      },
      next_payment: {
        due_date: b.next_payment.due_date || a.next_payment.due_date,
        amount: b.next_payment.amount || a.next_payment.amount || 0,
        is_overdue: b.next_payment.is_overdue || a.next_payment.is_overdue,
        days_until_due:
          b.next_payment.days_until_due || a.next_payment.days_until_due || 0,
      },
      payment_sources: {
        system: {
          label: a.payment_sources.system.label,
          description: a.payment_sources.system.description,
          enabled: a.payment_sources.system.enabled,
          default_card:
            b.payment_sources.system.default_card ??
            a.payment_sources.system.default_card ??
            null,
        },
        establishment_balance: {
          label: a.payment_sources.establishment_balance.label,
          description: a.payment_sources.establishment_balance.description,
          enabled: a.payment_sources.establishment_balance.enabled,
          current_balance:
            a.payment_sources.establishment_balance.current_balance ||
            a.billing_config.available_balance ||
            b.payment_sources.establishment_balance.current_balance ||
            b.billing_config.available_balance ||
            0,
        },
      },
    };

    return { data: merged } as any;
  } catch (error) {
    return handleThunkError(
      error,
      thunkAPI,
      "Erro ao buscar configuração de billing",
    );
  }
});

export const fetchBillingPayments = createAsyncThunk<
  { data: BillingPaymentsResponse },
  BillingPaymentFilters | undefined
>("establishmentBilling/fetchPayments", async (filters, thunkAPI) => {
  try {
    const params: Record<string, string | number> = {};
    if (filters?.status) params.status = filters.status;
    if (filters?.start_date) params.start_date = filters.start_date;
    if (filters?.end_date) params.end_date = filters.end_date;
    if (filters?.per_page) params.per_page = filters.per_page;
    if (filters?.page) params.page = filters.page;

    const response = await axiosInstance.get("/billing/payments", {
      params,
    });
    return response.data;
  } catch (error) {
    return handleThunkError(error, thunkAPI, "Erro ao buscar pagamentos");
  }
});

export const fetchBillingStatus = createAsyncThunk<
  { data: BillingStatus },
  void
>("establishmentBilling/fetchStatus", async (_: void, thunkAPI) => {
  try {
    const response = await axiosInstance.get("/billing/status");
    return response.data;
  } catch (error) {
    return handleThunkError(
      error,
      thunkAPI,
      "Erro ao buscar status de billing",
    );
  }
});

export const createBillingToken = createAsyncThunk<
  BillingTokenStatusResponse,
  AsaasSubaccountCreatePayload
>("establishmentBilling/createToken", async (payload, thunkAPI) => {
  try {
    const response = await axiosInstance.post(
      "/billing/asaas/subaccount",
      payload,
    );
    return response.data;
  } catch (error) {
    return handleThunkError(error, thunkAPI, "Erro ao criar conta bancária");
  }
});

export const fetchBillingTokenStatus = createAsyncThunk<
  BillingTokenStatusResponse,
  void
>("establishmentBilling/fetchTokenStatus", async (_: void, thunkAPI) => {
  try {
    const response = await axiosInstance.get("/billing/token-status");

    return response.data;
  } catch (error) {
    return handleThunkError(
      error,
      thunkAPI,
      "Erro ao buscar status do token de billing",
    );
  }
});

export const fetchBillingDocuments = createAsyncThunk<
  AsaasDocumentsResponse,
  void
>("establishmentBilling/fetchDocuments", async (_: void, thunkAPI) => {
  try {
    const response = await axiosInstance.get("/billing/asaas/documents");
    const payload = response?.data?.data ?? response?.data;
    return normalizeDocumentsPayload(payload);
  } catch (error) {
    return handleThunkError(
      error,
      thunkAPI,
      "Erro ao carregar documentos pendentes",
    ) as never;
  }
});

export const uploadBillingDocument = createAsyncThunk<
  AsaasDocumentsResponse,
  UploadBillingDocumentPayload
>("establishmentBilling/uploadDocument", async (payload, thunkAPI) => {
  const { documentId, file, type } = payload;
  try {
    const formData = new FormData();
    formData.append("documentFile", file);
    if (type) {
      formData.append("type", type);
    }

    const response = await axiosInstance.post(
      `/billing/asaas/documents/${documentId}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    const data = response?.data?.data ?? response?.data;
    return normalizeDocumentsPayload(data);
  } catch (error) {
    return handleThunkError(
      error,
      thunkAPI,
      "Erro ao enviar documento",
    ) as never;
  }
});

export const fetchBillingKycStatus = createAsyncThunk<AsaasKycStatus, void>(
  "establishmentBilling/fetchKycStatus",
  async (_: void, thunkAPI) => {
    try {
      const response = await axiosInstance.get("/billing/asaas/status");
      return response?.data?.data ?? response?.data;
    } catch (error) {
      return handleThunkError(
        error,
        thunkAPI,
        "Erro ao consultar situação cadastral",
      ) as never;
    }
  },
);

export const processBillingPayment = createAsyncThunk<
  { data: ProcessPaymentResponse; message?: string },
  ProcessPaymentRequest
>("establishmentBilling/processPayment", async (data, thunkAPI) => {
  try {
    const response = await axiosInstance.post(
      "/billing/payments/process",
      data,
    );
    return response.data;
  } catch (error) {
    return handleThunkError(error, thunkAPI, "Erro ao processar pagamento");
  }
});

export const updateBillingConfig = createAsyncThunk<
  { data: EstablishmentBillingConfig; message?: string },
  UpdateBillingConfigRequest
>("establishmentBilling/updateConfig", async (data, thunkAPI) => {
  try {
    const response = await axiosInstance.put("/billing/config", data);
    return response.data;
  } catch (error) {
    return handleThunkError(error, thunkAPI, "Erro ao atualizar configuração");
  }
});

// ===== CARDS =====
export const fetchBillingCards = createAsyncThunk<
  { data: BillingCard[] },
  void
>("establishmentBilling/fetchCards", async (_: void, thunkAPI) => {
  try {
    const response = await axiosInstance.get("/billing/cards");
    // Normalize various possible backend shapes into BillingCard[]
    const raw =
      response?.data?.data?.cards ??
      response?.data?.data ??
      response?.data?.cards ??
      response?.data ??
      [];

    const cards: BillingCard[] = Array.isArray(raw)
      ? raw.map((c: any) => ({
          id: c.id,
          brand: c.brand,
          last_digits: c.last_digits ?? c.last4 ?? "",
          month_due: String(c.month_due ?? c.exp_month ?? ""),
          year_due: String(c.year_due ?? c.exp_year ?? ""),
          is_default: Boolean(c.is_default),
          card_name: c.card_name ?? undefined,
        }))
      : [];

    return { data: cards } as any;
  } catch (error) {
    return handleThunkError(error, thunkAPI, "Erro ao buscar cartões");
  }
});

export const createBillingCard = createAsyncThunk<
  { data: BillingCard; message?: string },
  CreateBillingCardRequest
>("establishmentBilling/createCard", async (payload, thunkAPI) => {
  try {
    const response = await axiosInstance.post("/billing/cards", payload);
    const raw =
      response?.data?.data?.card ?? response?.data?.data ?? response?.data;
    const mapped: BillingCard = {
      id: raw.id,
      brand: raw.brand,
      last_digits: raw.last_digits ?? raw.last4 ?? "",
      month_due: String(raw.month_due ?? raw.exp_month ?? ""),
      year_due: String(raw.year_due ?? raw.exp_year ?? ""),
      is_default: Boolean(raw.is_default),
      card_name: raw.card_name ?? undefined,
    };
    return { data: mapped, message: response?.data?.message } as any;
  } catch (error) {
    return handleThunkError(error, thunkAPI, "Erro ao adicionar cartão");
  }
});

export const setDefaultBillingCard = createAsyncThunk<
  { data: BillingCard; message?: string },
  { cardId: number }
>("establishmentBilling/setDefaultCard", async ({ cardId }, thunkAPI) => {
  try {
    const response = await axiosInstance.patch(
      `/billing/cards/${cardId}/default`,
    );
    const raw =
      response?.data?.data?.card ?? response?.data?.data ?? response?.data;
    const mapped: BillingCard = {
      id: raw.id,
      brand: raw.brand,
      last_digits: raw.last_digits ?? raw.last4 ?? "",
      month_due: String(raw.month_due ?? raw.exp_month ?? ""),
      year_due: String(raw.year_due ?? raw.exp_year ?? ""),
      is_default: Boolean(raw.is_default),
      card_name: raw.card_name ?? undefined,
    };
    return { data: mapped, message: response?.data?.message } as any;
  } catch (error) {
    return handleThunkError(error, thunkAPI, "Erro ao definir cartão padrão");
  }
});

export const deleteBillingCard = createAsyncThunk<
  { data: { id: number }; message?: string },
  { cardId: number }
>("establishmentBilling/deleteCard", async ({ cardId }, thunkAPI) => {
  try {
    const response = await axiosInstance.delete(`/billing/cards/${cardId}`);
    return response.data;
  } catch (error) {
    return handleThunkError(error, thunkAPI, "Erro ao remover cartão");
  }
});
